import React, { useState, useEffect, useRef } from 'react';
import styles from '../styles/InstallHelper.module.css';
import buttonStyles from '../styles/Button.module.css';
import FaIcon from './FaIcon';
import {
  detectEnvironment,
  setupInstallPromptListener,
  removeInstallPromptCallback,
  isInstallPromptAvailable,
  triggerInstall,
  getInstallStrategy,
  markInstallHelperDismissed
} from '../utils/installHelper';

/**
 * InstallHelper Component
 * 
 * A reusable component that guides users to install the app as a PWA
 * or provides instructions for adding to home screen / bookmarks.
 * 
 * Props:
 * - mode: 'modal' | 'inline' | 'page' - affects layout/styling
 * - onClose: function - optional, called when modal is closed
 * - trackEvent: function(name, data) - optional analytics callback
 * - dismissOnInstall: boolean - whether to auto-close after install (default: true)
 */
const InstallHelper = ({ 
  mode = 'inline', 
  onClose, 
  trackEvent,
  dismissOnInstall = true 
}) => {
  const [env, setEnv] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [installAvailable, setInstallAvailable] = useState(false);
  const [installing, setInstalling] = useState(false);
  const hasTrackedShown = useRef(false);
  const envRef = useRef(null);
  
  // Initialize environment detection and prompt listener - runs once
  useEffect(() => {
    const detectedEnv = detectEnvironment();
    setEnv(detectedEnv);
    envRef.current = detectedEnv;
    
    // Check if install prompt is already available
    const promptAvailable = isInstallPromptAvailable();
    setInstallAvailable(promptAvailable);
    
    // Compute initial strategy
    const initialStrategy = getInstallStrategy(detectedEnv, promptAvailable);
    setStrategy(initialStrategy);
    
    // Callback for when install prompt becomes available
    const handleInstallAvailable = () => {
      setInstallAvailable(true);
      // Recompute strategy when native prompt becomes available
      if (envRef.current) {
        const newStrategy = getInstallStrategy(envRef.current, true);
        setStrategy(newStrategy);
      }
    };
    
    // Set up listener for future install prompt availability
    setupInstallPromptListener(handleInstallAvailable);
    
    return () => {
      removeInstallPromptCallback(handleInstallAvailable);
    };
  }, []); // Empty dependency array - runs only once
  
  // Track when component is shown
  useEffect(() => {
    if (strategy && trackEvent && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      trackEvent('install_helper_shown', {
        env: env?.label,
        strategy: strategy?.type,
        mode
      });
    }
  }, [strategy, trackEvent, env, mode]);
  
  // Handle install button click
  const handleInstallClick = async () => {
    if (installing) return;
    
    setInstalling(true);
    
    // Track click
    if (trackEvent) {
      trackEvent('install_click', { env: env?.label });
    }
    
    try {
      const outcome = await triggerInstall();
      
      // Track result
      if (trackEvent) {
        trackEvent('install_result', { env: env?.label, outcome });
      }
      
      // Close if install was accepted
      if (outcome === 'accepted' && dismissOnInstall && onClose) {
        markInstallHelperDismissed();
        onClose();
      }
    } catch (err) {
      console.warn('[InstallHelper] Install error:', err);
    } finally {
      setInstalling(false);
    }
  };
  
  // Don't render if already installed or no strategy
  if (!strategy) {
    return null;
  }
  
  // Determine container class based on mode
  const containerClass = [
    styles.container,
    mode === 'page' && styles.pageMode,
    mode === 'modal' && styles.modalMode,
    mode === 'inline' && styles.inlineMode
  ].filter(Boolean).join(' ');
  
  return (
    <div className={containerClass}>
      {/* Header with icon - hidden in modal mode (ModalContext provides header) */}
      {mode !== 'modal' && (
        <div className={styles.header}>
          <div className={styles.iconWrapper}>
            <FaIcon className="fa-solid fa-mobile-screen-button" />
          </div>
          <h2 className={styles.title}>{strategy.title}</h2>
        </div>
      )}
      
      {/* Icon for modal mode - shown above description */}
      {mode === 'modal' && (
        <div className={styles.iconWrapper} style={{ margin: '0 auto 20px' }}>
          <FaIcon className="fa-solid fa-download" />
        </div>
      )}
      
      {/* Description */}
      <p className={styles.description}>{strategy.description}</p>
      
      {/* Content: either Install button or step-by-step instructions */}
      {strategy.type === 'pwaPrompt' && installAvailable ? (
        <div className={styles.buttonContainer}>
          <button
            className={[buttonStyles.button, buttonStyles.primaryBlue].join(' ')}
            onClick={handleInstallClick}
            disabled={installing}
          >
            {installing ? (
              <>
                <FaIcon className="fa-solid fa-spinner fa-spin" />
                <span>Installazione...</span>
              </>
            ) : (
              <>
                <FaIcon className="fa-solid fa-download" />
                <span>{strategy.buttonText}</span>
              </>
            )}
          </button>
        </div>
      ) : strategy.steps ? (
        <ol className={styles.stepsList}>
          {strategy.steps.map((step, index) => (
            <li key={index} className={styles.step}>
              <span className={styles.stepNumber}>{index + 1}</span>
              <span className={styles.stepIcon}>
                <FaIcon className={`fa-solid ${step.icon}`} />
              </span>
              <span className={styles.stepText}>{step.text}</span>
            </li>
          ))}
        </ol>
      ) : null}
      
      {/* Visual guide for iOS */}
      {strategy.type === 'shareSheetInstructions' && (
        <div className={styles.visualGuide}>
          <div className={styles.iosShareIcon}>
            <FaIcon className="fa-solid fa-arrow-up-from-bracket" />
          </div>
          <span className={styles.visualGuideText}>
            Cerca questo pulsante nella barra del browser
          </span>
        </div>
      )}
      
      {/* Page mode back link */}
      {mode === 'page' && (
        <div className={styles.backLinkContainer}>
          <a href="/" className={styles.backLink}>
            <FaIcon className="fa-solid fa-arrow-left" />
            <span>Torna alla home</span>
          </a>
        </div>
      )}
    </div>
  );
};

export default InstallHelper;
